/** Conversion y formato de las unidades internas (mg y centimos) a texto. */

export const mgAGramos = (mg: number): number => Math.round(mg) / 1000;
export const gramosAMg = (gramos: number): number => Math.round(gramos * 1000);
export const centimosAEuros = (centimos: number): number => Math.round(centimos) / 100;
export const eurosACentimos = (euros: number): number => Math.round(euros * 100);

export function gramos(mg: number, decimales = 2): string {
  return `${mgAGramos(mg).toLocaleString("es-ES", {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimales,
  })} g`;
}

/** "g" para categorías que se pesan, "ud" para las que se cuentan. Acepta
 *  tanto la unidad de la categoría (PESO/UNIDAD) como la corta (g/ud). */
export function sufijoUnidad(unidad: string | undefined | null): "g" | "ud" {
  return unidad === "UNIDAD" || unidad === "ud" ? "ud" : "g";
}

/** Cantidad interna (mg o milesimas de unidad) formateada con su unidad. */
export function formatearCantidad(valor: number, unidad: string | undefined | null, decimales = 2): string {
  if (sufijoUnidad(unidad) === "ud") {
    // Unidades enteras: media galleta no existe, así que se redondea hacia abajo.
    return `${Math.floor(valor / 1000).toLocaleString("es-ES")} ud`;
  }
  return gramos(valor, decimales);
}

/**
 * La abreviatura que va pegada a cada importe del club, donde antes iba «€».
 *
 * Esta aquí sola para que cambiarla sea una línea: sale en tablas muy apretadas
 * del mostrador y en el móvil del socio, así que es corta a propósito.
 *
 * Es la misma en los cinco idiomas, igual que la «g» y el «ud» de aquí abajo.
 * En alemán lo propio sería «Cr», porque los sustantivos van en mayúscula y así
 * no se confunde con la «g» minúscula de los gramos que va en la misma línea;
 * se queda en minúscula a conciencia, porque «ud» tampoco se traduce y poner
 * «12,50 Cr» junto a «3 ud» quedaría peor que las dos en minúscula. Si algún
 * día se traducen las unidades, esta se traduce con ellas y no antes. La
 * palabra entera sí está en cada diccionario: créditos, crèdits, credits,
 * credits, Credits.
 */
export const UNIDAD_CREDITO = "cr";

/**
 * El dinero del club, en créditos.
 *
 * Un crédito vale un euro y por debajo se siguen guardando céntimos: las cuentas
 * no cambian, cambia cómo se nombra. Un club cannábico no vende y sus socios no
 * compran: aportan a la asociación y retiran producto, y el programa lo llama
 * como es.
 *
 * NO es lo mismo que `euros()`, y no se pueden mezclar: ver el comentario de
 * abajo.
 */
export function creditos(centimos: number): string {
  const importe = centimosAEuros(centimos).toLocaleString("es-ES", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  // Espacio duro: el importe y su unidad no pueden partirse en dos lineas.
  return `${importe}\u00a0${UNIDAD_CREDITO}`;
}

/**
 * Euros de verdad, y solo para lo que de verdad lo es.
 *
 * Son tres cosas, todas fuera del local: lo que asocis le factura al club, el
 * precio anunciado en la web pública y el panel de plataforma. Eso es una
 * relación comercial entre dos empresas y se factura en euros.
 *
 * Lo que sale del programa hacia la gestoría -los CSV- también va en euros, y
 * tiene su propio formateador en la API (`eurosCsv`). Si esto se usara dentro
 * del club, o `creditos()` aquí, el libro que el club entrega diría una unidad
 * que su gestoría no puede cuadrar.
 */
export function euros(centimos: number): string {
  return centimosAEuros(centimos).toLocaleString("es-ES", {
    style: "currency",
    currency: "EUR",
  });
}

export function fecha(valor: string | Date | null | undefined): string {
  if (!valor) return "—";
  return new Date(valor).toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export function fechaHora(valor: string | Date | null | undefined): string {
  if (!valor) return "—";
  return new Date(valor).toLocaleString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function hora(valor: string | Date | null | undefined): string {
  if (!valor) return "—";
  return new Date(valor).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
}

/**
 * La fecha en formato AAAA-MM-DD, **en la hora de quien mira la pantalla**.
 *
 * Con `toISOString()` salia la fecha UTC, y entre las doce y las dos de la
 * madrugada -que en un club es hora punta- eso es ayer: se registraba una
 * entrega y desaparecia de la lista del dia, porque la pantalla estaba pidiendo
 * el dia anterior.
 */
export function iso(valor: Date): string {
  const mes = String(valor.getMonth() + 1).padStart(2, "0");
  const dia = String(valor.getDate()).padStart(2, "0");
  return `${valor.getFullYear()}-${mes}-${dia}`;
}

/**
 * Días que faltan, por días NATURALES y no por horas (negativo si ya pasó).
 *
 * Las dos fechas se ponen a medianoche antes de restar, y de ahí el redondeo: el
 * día que los relojes atrasan tiene 25 horas y el que adelantan 23. Dividiendo
 * milisegundos a secas, del 16 al 26 de octubre salían 11 días cuando en el
 * calendario hay 10, y el socio leía «tu cuota vence en 11 días» con un carnet
 * que dice 26 de octubre. En la semana larga antes de cada cambio de hora, toda
 * cuenta atrás del programa decía un día de más.
 *
 * Es la misma regla que `diasEntre` en el servidor, y tiene que serlo: el socio
 * ve el número en su portal y lo recibe en el correo de la cuota, y los dos
 * tienen que decir lo mismo.
 */
export function diasHasta(valor: string | Date | null | undefined): number | null {
  if (!valor) return null;
  const objetivo = new Date(valor);
  if (Number.isNaN(objetivo.getTime())) return null;
  const hoy = new Date();
  const a = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
  const b = new Date(objetivo.getFullYear(), objetivo.getMonth(), objetivo.getDate());
  return Math.round((b.getTime() - a.getTime()) / (24 * 3600 * 1000));
}

export function iniciales(nombre: string, apellidos = ""): string {
  return `${nombre.charAt(0)}${apellidos.charAt(0)}`.toUpperCase();
}

export const ETIQUETA_ESTADO: Record<string, { texto: string; clase: string }> = {
  ACTIVO: { texto: "Activo", clase: "verde" },
  PENDIENTE: { texto: "Pendiente", clase: "ambar" },
  CADUCADO: { texto: "Caducado", clase: "roja" },
  SUSPENDIDO: { texto: "Suspendido", clase: "roja" },
  BAJA: { texto: "Baja", clase: "" },
};

export const ETIQUETA_ROL: Record<string, string> = {
  PROPIETARIO: "Propietario",
  TRABAJADOR: "Trabajador",
};

export const ETIQUETA_GENETICA: Record<string, string> = {
  SATIVA: "Sativa",
  INDICA: "Indica",
  HIBRIDA: "Hibrida",
  CBD: "CBD",
  NA: "—",
};

export const ETIQUETA_PAGO: Record<string, string> = {
  EFECTIVO: "Efectivo",
  CARTERA: "Cartera",
  TARJETA: "Tarjeta",
  TRANSFERENCIA: "Transferencia",
};

export const ETIQUETA_MOVIMIENTO: Record<string, string> = {
  ENTRADA: "Entrada",
  DISPENSACION: "Dispensacion",
  MERMA: "Merma",
  AJUSTE: "Ajuste",
  DEVOLUCION: "Devolucion",
  RECARGA: "Recarga",
  CONSUMO: "Consumo",
  CUOTA: "Cuota",
  SALIDA: "Salida",
  GASTO: "Gasto",
};

/**
 * Concuerda un nombre con su número: 1 entrega, 2 entregas.
 *
 *   plural(1, "entrega")            -> "1 entrega"
 *   plural(3, "entrega")            -> "3 entregas"
 *   plural(1, "mes", "meses")       -> "1 mes"
 *
 * Por defecto añade una "s", que vale para casi todo. Cuando no valga, se pasa
 * el plural a mano. Escribir "1 entregas" hace que el programa parezca a medio
 * hacer, y es de las primeras cosas que nota quien lo va a comprar.
 */
export function plural(cantidad: number, singular: string, formaPlural?: string): string {
  const palabra = cantidad === 1 ? singular : (formaPlural ?? `${singular}s`);
  return `${cantidad} ${palabra}`;
}
